import { useEffect } from 'react';
import { Navigate, Outlet } from 'react-router';

import { RouteDecisionSkeleton } from '@/components/feedback/route-decision-skeleton';
import { useOnboardingState } from '@/hooks/use-current-user';
import { errorIds, logError } from '@/lib/log-error';

/**
 * The profile step of the router gating chain (ESCLOUD-333); the chain itself is
 * declared once in `GATING_CHAIN` (`router/index.tsx`).
 *
 * A tenant member whose professional profile the server calls incomplete is
 * sent to `/welcome/profile`, once: completing it flips the server's answer and
 * nothing here remembers a visit. Completeness is never re-derived on the
 * client: `GET /users/me/onboarding-state` recomputes it from the current
 * required-field list, the same one the clinical gate enforces.
 *
 * Mounted under `SubscriptionGuard` and above `DeviceUseGate` on the device
 * pages only. Settings, billing and the user's own profile stay outside, so a
 * buyer can always reach billing whatever the state of their profile.
 *
 * An answer that cannot be obtained renders the route (ESCLOUD-268): the
 * device-use gate below fails closed and the API refuses clinical calls
 * regardless, so failing open here costs nothing and never strands a user.
 */
export function ProfileGuard() {
    const { data, isPending, isError, error } = useOnboardingState();

    useEffect(() => {
        if (isError) {
            logError(
                errorIds.PROFILE_GUARD_UNAVAILABLE,
                'Onboarding state unavailable: rendering the route',
                { error }
            );
        }
    }, [isError, error]);

    if (isPending) {
        return <RouteDecisionSkeleton />;
    }

    if (isError || !data) {
        return <Outlet />;
    }

    if (!data.profileComplete) {
        return <Navigate to="/welcome/profile" replace />;
    }

    return <Outlet />;
}
